import { ReactNode, createContext, useContext } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useSecurityScore } from '../../hooks/useSecurityScore';
import { useNpsScore } from '../../hooks/useNpsScore';
import { useSonarQubeScore } from '../../hooks/useSonarQubeScore';
import {
  discoveryApiRef,
  identityApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';

type ScoreCardContextType = {
  security: {
    score: number;
    date: string;
    checks: number;
    loading: boolean;
    error: string | null;
  };
  nps: {
    score: number;
    date: string;
    responses: number;
    loading: boolean;
    error: string | null;
  };
  sonar: {
    score: number;
    date: string;
    checks: number;
    loading: boolean;
    error: string | null;
    projectKeyMissing: boolean;
  };
};

const ScoreCardContext = createContext<ScoreCardContextType | undefined>(
  undefined,
);

export const ScoreCardProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const fetchApi = useApi(fetchApiRef);

  const {
    score: securityScore,
    date: securityDate,
    checks: securityChecks,
    loading: securityLoading,
    error: securityError,
  } = useSecurityScore();

  const {
    score: npsScore,
    date: npsDate,
    responses: npsResponses,
    loading: npsLoading,
    error: npsError,
  } = useNpsScore();

  const projectKey = entity.metadata.annotations?.['sonarqube.org/project-key'];
  let sonarScore = 0;
  let sonarDate = '';
  let sonarChecks = 0;
  let sonarLoading = false;
  let sonarError: string | null = null;
  let projectKeyMissing = false;

  const sonarData = useSonarQubeScore(
    entity,
    discoveryApi,
    identityApi,
    fetchApi,
  );
  // console.log('Project Key:', projectKey);
  if (!projectKey) {
    projectKeyMissing = true;
    sonarError = 'Project key is not set for this repo'; // optional: keep or clear this
  } else {
    sonarScore = sonarData.score;
    sonarDate = sonarData.date;
    sonarChecks = sonarData.checks;
    sonarLoading = sonarData.loading;
    sonarError = sonarData.error;
  }

  return (
    <ScoreCardContext.Provider
      value={{
        security: {
          score: securityScore,
          date: securityDate,
          checks: securityChecks.length,
          loading: securityLoading,
          error: securityError,
        },
        nps: {
          score: npsScore,
          date: npsDate,
          responses: npsResponses,
          loading: npsLoading,
          error: npsError,
        },
        sonar: {
          score: sonarScore,
          date: sonarDate,
          checks: sonarChecks,
          loading: sonarLoading,
          error: sonarError,
          projectKeyMissing,
        },
      }}
    >
      {children}
    </ScoreCardContext.Provider>
  );
};

export const useScorecard = (): ScoreCardContextType => {
  const ctx = useContext(ScoreCardContext);
  if (!ctx) {
    throw new Error('useScorecard must be used within a ScoreCardProvider');
  }
  return ctx;
};
